import {
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { LocationDescriptor as To } from "history";
import { parse, stringify } from "query-string";
import React from "react";

import { Btn, BtnLayout, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { Form } from "@/mk2/components/Form";
import { Link } from "@/mk2/components/Link";
import { scrollDocumentTo } from "@/mk2/helpers/scrolling";
import { removeEmpty, removePage1 } from "@/mk2/helpers/urls";
import {
    HistoryLocationState,
    HistoryLocationWithState,
    RouteNavigateFunction,
} from "@/mk2/services/browserHistory";
import {
    BZ2_GO_BOTTOM,
    BZ2_GO_NEXT,
    BZ2_GO_PREVIOUS,
    BZ2_GO_TOP,
    CANCEL,
    GO_PAGING,
    OUT_OF,
    PAGE,
} from "@/mk/autogenerated/translations/Pager.a4c1c57cc6aa3cc6ff81c761312ab775";

import styles from "./Pager.mscss";

const fasChevronDownLg: FontAwesomeIconProps = { icon: faChevronDown, size: "lg" };
const fasChevronLeftLg: FontAwesomeIconProps = { icon: faChevronLeft, size: "lg" };
const fasChevronRightLg: FontAwesomeIconProps = { icon: faChevronRight, size: "lg" };
const fasChevronUpLg: FontAwesomeIconProps = { icon: faChevronUp, size: "lg" };

interface OwnProps {
    id?: string;
    className?: string;
    disableHasNext?: boolean; // used for countless pager, when hasNext cannot be deduced from "page < numberOfPages"
    page: number;
    numberOfPages: number; // undefined for countless pager
    location: HistoryLocationWithState;
    routeNavigate: RouteNavigateFunction;
    position: "top" | "bottom";
    isMobile: boolean;

    to?(page: number): [To, Partial<HistoryLocationState>];
}

interface State {
    page: string;
    showFormControls: boolean;
}

type Props = OwnProps;

const url = (base: string, params: any) => {
    const search = stringify(params);
    return base + (search ? `?${search}` : "");
};

export class Pager extends React.Component<Props, State> {
    private inputElem: HTMLInputElement;

    constructor(props: Props) {
        super(props);

        this.state = {
            page: props.page ? props.page.toString() : null,
            showFormControls: false,
        };
    }

    public shouldComponentUpdate(n: Props, ns: State) {
        const p = this.props;
        const ps = this.state;

        return (
            n.page !== p.page ||
            n.numberOfPages !== p.numberOfPages ||
            n.to !== p.to ||
            ns.showFormControls !== ps.showFormControls ||
            ns.page !== ps.page
        );
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Reset state when new page is sent from parent
        if (this.props.page !== nextProps.page) {
            this.setState({
                page: nextProps.page ? nextProps.page.toString() : null,
                showFormControls: false,
            });
        }
    }

    public render() {
        const {
            id,
            className,
            disableHasNext,
            numberOfPages,
            page,
            location,
            position,
            isMobile,
            to,
            routeNavigate,
        } = this.props;

        const query = parse(location.search);

        const hasNext = !disableHasNext && (numberOfPages === undefined || page < numberOfPages);
        const hasPrevious = page > 1;

        const toLast = to ? to(numberOfPages) : undefined;
        const pageForm = (
            <span className={styles.Pager__pageForm}>
                {PAGE}{" "}
                <Form onSubmit={this.onSubmit}>
                    <input
                        className={styles.Pager__pageForm__input}
                        type="text"
                        name="page"
                        value={this.state.page}
                        onChange={this.onChange}
                        onFocus={this.onFocus}
                        ref={this.handleInputRef}
                        aria-label="Page number"
                    />
                    {this.state.showFormControls && (
                        <span className={styles.Pager__pageForm__controls}>
                            <Btn type={BtnType.Blue} label={GO_PAGING} onClick={this.onSubmit} />
                            <Btn type={BtnType.Link} label={CANCEL} onClick={this.onCancel} />
                        </span>
                    )}
                </Form>{" "}
                {!this.state.showFormControls && numberOfPages !== undefined ? (
                    <div>
                        {OUT_OF}
                        {page !== numberOfPages ? (
                            <Link
                                className={styles.Pager__pageForm__numberOfPages}
                                to={
                                    toLast
                                        ? toLast[0]
                                        : {
                                              ...location,
                                              search: stringify({
                                                  ...query,
                                                  page: numberOfPages,
                                                  scrollto:
                                                      position === "bottom"
                                                          ? // odscrolluj ma na prvy prispevok strany kam idem
                                                            "first"
                                                          : // zachovaj scroll position, iba sa dole pod nami prekreslia prispevky
                                                            null,
                                              }),
                                          }
                                }
                                state={
                                    toLast
                                        ? toLast[1]
                                        : {
                                              // chceme aby sa stranka neodscrollovala a bud
                                              // 1) ostala na tom istom mieste
                                              // 2) alebo po nacitani sami dalsim kodom odscrollujeme na prvy prispevok
                                              dontScrollOnPush: true,
                                              // ked chodim medzi strankami cez prev/next a potom dam 'back',
                                              // tak sa chcem vratit na stranku odkial som prisiel a nie na
                                              // tuto istu stranku len s inym ?page=...
                                              skipOnBack: true,
                                          }
                                }
                            >
                                {numberOfPages}
                            </Link>
                        ) : (
                            <span className={styles.Pager__pageForm__numberOfPages}>
                                {numberOfPages}
                            </span>
                        )}
                    </div>
                ) : null}
            </span>
        );

        const toPrev = to ? to(Math.max(page - 1, 1)) : undefined;
        const toNext = to ? to(page + 1) : undefined;
        return (
            <div id={id}>
                {(numberOfPages === undefined || numberOfPages > 1) && (
                    <div className={cx(styles.Pager, className)}>
                        {position === "top" && (
                            <Btn
                                type={BtnType.BlueTextLight}
                                icon={fasChevronDownLg}
                                label={!isMobile && BZ2_GO_BOTTOM}
                                onClick={this.onDownScroll}
                                sidePadding={BtnSidePadding.Right}
                                className={styles.Pager__leftBtn}
                                classNames={{ icon: !isMobile && styles.Pager__iconDesktop }}
                                data-cy="pager-go-bottom"
                                aria-label="Bottom"
                            />
                        )}
                        {position === "bottom" && (
                            <Btn
                                type={BtnType.BlueTextLight}
                                icon={fasChevronUpLg}
                                label={!isMobile && BZ2_GO_TOP}
                                onClick={this.onUpScroll}
                                sidePadding={BtnSidePadding.Right}
                                className={styles.Pager__leftBtn}
                                classNames={{ icon: !isMobile && styles.Pager__iconDesktop }}
                                data-cy="pager-go-top"
                                aria-label="Top"
                            />
                        )}

                        <div className={styles.Pager__spring} />

                        {!(isMobile && this.state.showFormControls) && (
                            <Btn
                                type={BtnType.BlueTextLight}
                                icon={fasChevronLeftLg}
                                classNames={{ icon: !isMobile && styles.Pager__iconDesktop }}
                                label={!isMobile && BZ2_GO_PREVIOUS}
                                link={
                                    toPrev
                                        ? toPrev[0]
                                        : {
                                              pathname: location.pathname,
                                              search: stringify(
                                                  removeEmpty(
                                                      removePage1({
                                                          ...query,
                                                          page: Math.max(page - 1, 1),
                                                          scrollto:
                                                              position === "bottom"
                                                                  ? // odscrolluj ma na prvy prispevok strany kam idem
                                                                    "first"
                                                                  : // zachovaj scroll position, iba sa dole pod nami prekreslia prispevky
                                                                    null,
                                                      }),
                                                  ),
                                              ),
                                          }
                                }
                                linkState={
                                    toPrev
                                        ? toPrev[1]
                                        : {
                                              // chceme aby sa stranka neodscrollovala a bud
                                              // 1) ostala na tom istom mieste
                                              // 2) alebo po nacitani sami dalsim kodom odscrollujeme na prvy prispevok
                                              dontScrollOnPush: true,
                                              // ked chodim medzi strankami cez prev/next a potom dam 'back',
                                              // tak sa chcem vratit na stranku odkial som prisiel a nie na
                                              // tuto istu stranku len s inym ?page=...
                                              skipOnBack: true,
                                          }
                                }
                                linkRel="prev"
                                disabled={!hasPrevious}
                                data-cy="pager-previous"
                                aria-label="Previous"
                            />
                        )}

                        <div className={styles.Pager__pagingNumbers}>{pageForm}</div>

                        {!(isMobile && this.state.showFormControls) && (
                            <Btn
                                type={BtnType.BlueTextLight}
                                layout={BtnLayout.LabelIcon}
                                icon={fasChevronRightLg}
                                classNames={{ icon: !isMobile && styles.Pager__iconDesktop }}
                                label={!isMobile && BZ2_GO_NEXT}
                                sidePadding={BtnSidePadding.Left}
                                link={
                                    toNext
                                        ? toNext[0]
                                        : {
                                              pathname: location.pathname,
                                              search: stringify(
                                                  removeEmpty(
                                                      removePage1({
                                                          ...query,
                                                          page: page + 1,
                                                          scrollto:
                                                              position === "bottom"
                                                                  ? // odscrolluj ma na prvy prispevok strany kam idem
                                                                    "first"
                                                                  : // zachovaj scroll position, iba sa dole pod nami prekreslia prispevky
                                                                    null,
                                                      }),
                                                  ),
                                              ),
                                          }
                                }
                                linkState={
                                    toNext
                                        ? toNext[1]
                                        : {
                                              // chceme aby sa stranka neodscrollovala a bud
                                              // 1) ostala na tom istom mieste
                                              // 2) alebo po nacitani sami dalsim kodom odscrollujeme na prvy prispevok
                                              dontScrollOnPush: true,
                                              // ked chodim medzi strankami cez prev/next a potom dam 'back',
                                              // tak sa chcem vratit na stranku odkial som prisiel a nie na
                                              // tuto istu stranku len s inym ?page=...
                                              skipOnBack: true,
                                          }
                                }
                                linkRel="next"
                                disabled={!hasNext}
                                className={styles.Pager__rightBtn}
                                data-cy="pager-next"
                                aria-label="Next"
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }

    private onSubmit = (ev) => {
        ev.preventDefault(); // treba aby sa na Enter nerobil page-reload

        const { location, numberOfPages, position, to, routeNavigate } = this.props;
        const query = parse(location.search);

        let page = parseFloat(this.state.page);
        if (isNaN(page)) {
            return;
        }

        if (page < 1) {
            page = 1;
        }
        if (page > numberOfPages) {
            page = numberOfPages;
        }

        // remove focus from input
        this.inputElem.blur();

        if (page === this.props.page) {
            // Ignore if actual page is the same
            this.setState({
                showFormControls: false,
            });
            return;
        }

        if (to) {
            const [destTo, destState] = to(page);
            routeNavigate(destTo, {
                state: {
                    ...(typeof destTo !== "string" ? destTo.state : undefined),
                    ...destState,
                },
            });
        } else {
            routeNavigate(
                url(
                    location.pathname,
                    removeEmpty(
                        removePage1({
                            ...query,
                            page,
                            scrollto:
                                position === "bottom"
                                    ? // odscrolluj ma na prvy prispevok strany kam idem
                                      "first"
                                    : // zachovaj scroll position, iba sa dole pod nami prekreslia prispevky
                                      null,
                        }),
                    ),
                ),
                {
                    state: {
                        // chceme aby sa stranka neodscrollovala a bud
                        // 1) ostala na tom istom mieste
                        // 2) alebo po nacitani sami dalsim kodom odscrollujeme na prvy prispevok
                        dontScrollOnPush: true,
                        // ked chodim medzi strankami cez prev/next a potom dam 'back',
                        // tak sa chcem vratit na stranku odkial som prisiel a nie na
                        // tuto istu stranku len s inym ?page=...
                        skipOnBack: true,
                    },
                },
            );
        }
    };

    private onChange = (event: React.FormEvent<HTMLInputElement>) => {
        this.setState({
            page: event.currentTarget.value,
        });
    };

    private onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        // Select text
        event.currentTarget.select();

        this.setState({
            showFormControls: true,
        });
    };

    private onCancel = () => {
        this.setState({
            showFormControls: false,
            page: this.props.page ? this.props.page.toString() : null,
        });
    };

    private onUpScroll = () => {
        scrollDocumentTo("top", 0, true);
    };

    private onDownScroll = () => {
        scrollDocumentTo("bottom", 0, true);
    };

    private handleInputRef = (ref: HTMLInputElement) => {
        this.inputElem = ref;
    };
}
